import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Trash2, Loader2, Search, X, FolderOpen, Play,
  ScrollText, Gamepad2, Layers, FileText, BookOpen, ClipboardList, Trophy,
} from "lucide-react";
import QuizOverlay from "@/components/quiz/QuizOverlay";
import StoryQuizPlayer from "@/components/quiz/StoryQuizPlayer";
import AnimatedStoryOverlay from "@/components/story/AnimatedStoryOverlay";
import { StudyOverlay } from "@/components/cards/SmartCardsBody";
import { ChampStudentPlayer } from "@/components/game/MillionaireGame";

const TYPES = {
  quiz: { label: "Quiz", color: "#7C3AED", icon: ScrollText },
  game: { label: "Game", color: "#A855F7", icon: Gamepad2 },
  millionaire: { label: "Champ Game", color: "#F59E0B", icon: Trophy },
  story: { label: "Story", color: "#F97316", icon: BookOpen },
  cards: { label: "Smart Cards", color: "#EC4899", icon: Layers },
  slides: { label: "Slides", color: "#0891B2", icon: FileText },
  lesson_plan: { label: "Lesson Plan", color: "#0EA5E9", icon: ClipboardList },
  homework: { label: "Workbook", color: "#10B981", icon: ClipboardList },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function CreationsBody({ subject, chapter }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [quizPlay, setQuizPlay] = useState(null);
  const [storyQuizPlay, setStoryQuizPlay] = useState(null);
  const [storyPlay, setStoryPlay] = useState(null);
  const [deckPlay, setDeckPlay] = useState(null);
  const [champPlay, setChampPlay] = useState(null);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ai/creations", { params: { subject_id: subject.id } });
        setItems(data.items || []);
      } catch { setItems([]); }
    })();
  }, [subject.id]);

  const openItem = async (item) => {
    if (item.type === "slides") {
      navigate(`/teacher/subjects/${subject.id}/slides/${item.id}`);
      return;
    }
    setBusyId(item.id);
    try {
      const { data } = await api.get(`/ai/creations/${item.type}/${item.id}`);
      const doc = data.item;
      if (item.type === "quiz") {
        const qs = doc.questions || [];
        if (!qs.length) return toast.error("This quiz has no questions");
        const interactive =
          (doc.category === "mcq" && qs.some((x) => typeof x.correct_index === "number")) ||
          (doc.category === "drag_drop" && qs.some((x) => Array.isArray(x.items))) ||
          (doc.category === "fill_blank" && qs.some((x) => typeof x.sentence === "string"));
        if (interactive) setQuizPlay(doc); else setStoryQuizPlay(doc);
      } else if (item.type === "millionaire") setChampPlay(doc);
      else if (item.type === "story") setStoryPlay(doc);
      else if (item.type === "cards") setDeckPlay(doc);
      else setViewer({ ...doc, __type: item.type });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open"); }
    finally { setBusyId(null); }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.title}" permanently?`)) return;
    try {
      await api.delete(`/ai/creations/${item.type}/${item.id}`);
      setItems((cur) => cur.filter((x) => !(x.id === item.id && x.type === item.type)));
      toast.success("Deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const counts = {};
  (items || []).forEach((i) => { counts[i.type] = (counts[i.type] || 0) + 1; });
  const filtered = (items || []).filter((i) => {
    if (filter !== "all" && i.type !== filter) return false;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      return `${i.title} ${i.topic} ${TYPES[i.type]?.label || ""}`.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="creations-body">
      <div className="mb-1">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] font-bold" style={{ color: "#10B981" }}>My Creations</div>
        <div className="font-display font-bold text-xl tracking-tighter">Everything you've generated — saved for reuse</div>
        <p className="text-sm text-neutral-500 mt-1">Quizzes, games, stories, smart cards, slides, lesson plans and workbooks · replay any of them without spending credits.</p>
      </div>

      {/* Filter chips + search */}
      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={`All · ${(items || []).length}`} color="#0A0C10" testid="creations-filter-all" />
        {Object.entries(TYPES).map(([k, t]) => counts[k] ? (
          <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} label={`${t.label} · ${counts[k]}`} color={t.color} testid={`creations-filter-${k}`} />
        ) : null)}
        <div className="relative ml-auto">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search creations…" data-testid="creations-search"
            className="pl-8 pr-3 py-1.5 rounded-md border themed-border text-xs outline-none focus:border-[#0A0C10] bg-white w-44"
          />
        </div>
      </div>

      {/* List */}
      {items === null ? (
        <div className="mt-6 text-xs text-neutral-500 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading your creations…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 border-2 border-dashed border-neutral-200 rounded-xl p-10 text-center" data-testid="creations-empty">
          <FolderOpen className="mx-auto text-neutral-300" size={28} />
          <p className="mt-3 text-sm text-neutral-500">
            {(items || []).length === 0 ? "Nothing generated yet — every quiz, game, story or lesson plan you create lands here automatically." : "No creations match this filter."}
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-2 max-w-4xl">
          {filtered.map((item) => {
            const t = TYPES[item.type] || TYPES.quiz;
            const Icon = t.icon;
            const isChapter = item.chapter_id && item.chapter_id === chapter?.id;
            return (
              <div key={`${item.type}-${item.id}`} className="flex items-center gap-3 bg-white border border-black/8 rounded-lg px-4 py-3 hover:shadow-sm transition-shadow" data-testid={`creation-${item.type}-${item.id}`}>
                <div className="w-8 h-8 rounded-md inline-flex items-center justify-center shrink-0" style={{ background: t.color + "18", color: t.color }}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded shrink-0" style={{ background: t.color + "20", color: t.color }}>{t.label}</span>
                    <div className="text-sm font-semibold truncate">{item.title}</div>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 mt-0.5">
                    {item.meta} · <span className="text-neutral-500">{fmtDate(item.created_at)}</span>
                    {isChapter && <span style={{ color: t.color }}> · this chapter</span>}
                  </div>
                </div>
                <button onClick={() => openItem(item)} disabled={busyId === item.id} data-testid={`creation-open-${item.type}-${item.id}`}
                  className="text-xs font-bold px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 shrink-0"
                  style={{ background: t.color }}>
                  {busyId === item.id ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
                  {item.type === "lesson_plan" || item.type === "homework" ? "View" : "Play"}
                </button>
                <button onClick={() => del(item)} data-testid={`creation-del-${item.type}-${item.id}`}
                  className="p-1.5 text-neutral-400 hover:text-red-500 rounded hover:bg-red-50 shrink-0"><Trash2 size={13} /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* Players / overlays */}
      {quizPlay && (
        <QuizOverlay open onClose={() => setQuizPlay(null)} chapter={chapter} subject={subject} savedQuiz={quizPlay} />
      )}
      {storyQuizPlay && (
        <StoryQuizPlayer open onClose={() => setStoryQuizPlay(null)} quiz={storyQuizPlay} timePerQ={60} />
      )}
      {storyPlay && (
        <AnimatedStoryOverlay open loading={false} story={storyPlay} onClose={() => setStoryPlay(null)} />
      )}
      {deckPlay && <StudyOverlay deck={deckPlay} onClose={() => setDeckPlay(null)} />}
      {champPlay && <ChampStudentPlayer game={champPlay} onClose={() => setChampPlay(null)} playerName="Teacher" />}
      {viewer && <CreationViewer doc={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function FilterChip({ active, onClick, label, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1.5 rounded-md border transition-colors ${active ? "text-white" : "bg-white text-neutral-600 hover:border-neutral-400"}`}
      style={active ? { background: color, borderColor: color } : { borderColor: "#00000014" }}>
      {label}
    </button>
  );
}

// Read-only viewer for lesson plans, workbooks and simple games
function CreationViewer({ doc, onClose }) {
  const kind = doc.__type;
  const t = TYPES[kind] || TYPES.lesson_plan;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-lg border themed-border shadow-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="creation-viewer">
        <div className="px-5 py-4 border-b themed-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold" style={{ color: t.color }}>{t.label}</div>
            <div className="font-display font-bold text-lg tracking-tighter mt-0.5 truncate">
              {doc.title || (kind === "lesson_plan" ? `Lesson Plan — ${doc.subject || ""}` : "Untitled")}
            </div>
            <div className="text-[10px] font-mono text-neutral-400 mt-0.5">Generated {fmtDate(doc.created_at)}</div>
          </div>
          <button onClick={onClose} data-testid="creation-viewer-close" className="text-neutral-400 hover:text-[#0A0C10]"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {kind === "lesson_plan" && (
            <div className="whitespace-pre-wrap text-sm text-[#0A0C10] leading-relaxed">{doc.plan_content || "No content"}</div>
          )}
          {kind === "homework" && <HomeworkView data={doc.data || {}} />}
          {kind === "game" && <GameView data={doc.data || {}} />}
        </div>
      </div>
    </div>
  );
}

function HomeworkView({ data }) {
  const qs = data.questions || [];
  return (
    <div>
      {data.instructions && <div className="text-xs text-neutral-600 italic mb-3 bg-[#F0FDF4] border border-[#10B981]/20 rounded-md p-3">{data.instructions}</div>}
      <ol className="space-y-3">
        {qs.map((q, i) => (
          <li key={i} className="flex gap-3">
            <span className="text-[10px] font-mono font-bold text-[#10B981] shrink-0 pt-0.5 w-6">Q{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-neutral-900">{q.q || q.question}</div>
              {Array.isArray(q.options) && (
                <ul className="mt-1 space-y-0.5 text-xs text-neutral-600">
                  {q.options.map((o, k) => <li key={k}>{String.fromCharCode(65 + k)}. {o}</li>)}
                </ul>
              )}
              {q.answer && <div className="text-[11px] text-[#10B981] mt-1 font-medium">Answer: {q.answer}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function GameView({ data }) {
  if (Array.isArray(data.cards) && data.cards.length) {
    return (
      <div className="grid sm:grid-cols-2 gap-2">
        {data.cards.map((c, i) => (
          <div key={i} className="bg-[#FAF5FF] border border-[#A855F7]/20 rounded-md p-3">
            <div className="text-sm font-semibold text-[#0A0C10]">{c.front}</div>
            <div className="text-xs text-neutral-600 mt-1">{c.back}</div>
            {c.hint && <div className="text-[10px] text-[#A855F7] mt-1 italic">Hint: {c.hint}</div>}
          </div>
        ))}
      </div>
    );
  }
  if (Array.isArray(data.pairs) && data.pairs.length) {
    return (
      <div className="space-y-1.5">
        {data.pairs.map((p, i) => (
          <div key={i} className="flex items-center gap-3 bg-[#FAF5FF] border border-[#A855F7]/20 rounded-md px-3 py-2 text-sm">
            <span className="font-semibold flex-1">{p.left}</span>
            <span className="text-neutral-400">↔</span>
            <span className="text-neutral-700 flex-1 text-right">{p.right}</span>
          </div>
        ))}
      </div>
    );
  }
  if (Array.isArray(data.rounds) && data.rounds.length) {
    return (
      <ol className="space-y-3">
        {data.rounds.map((r, i) => (
          <li key={i}>
            <div className="text-sm font-semibold">R{i + 1}. {r.question}</div>
            <ul className="mt-1 space-y-0.5 text-xs">
              {(r.options || []).map((o, k) => (
                <li key={k} className={k === r.correct_index ? "text-[#10B981] font-semibold" : "text-neutral-600"}>
                  {String.fromCharCode(65 + k)}. {o}{k === r.correct_index ? " ✓" : ""}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    );
  }
  return <div className="text-sm text-neutral-500">This game has no playable content.</div>;
}
